import Vue from 'vue'
import App from './App.vue'
import router from './router'
import axios from 'axios'
import store from '@/core/services/store'
import ApiService from '@/core/services/api.service'
import { LOGOUT } from '@/core/services/store/auth.module.js'
import { RESET_LAYOUT_CONFIG, HANDLE_BACKEND_VERSION } from '@/core/services/store/config.module'

// Global 3rd party plugins
import * as Sentry from '@sentry/vue'
import { BrowserTracing } from '@sentry/tracing'
import PerfectScrollbar from 'perfect-scrollbar'
import { ValidationProvider, ValidationObserver, extend, localize } from 'vee-validate'
import * as rules from 'vee-validate/dist/rules'
import en from 'vee-validate/dist/locale/en.json'
import VWave from 'v-wave'
import VuePluralize from 'vue-pluralize'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import Multiselect from 'vue-multiselect'
import '@/core/plugins/portal-vue'
import '@/core/plugins/bootstrap-vue'
import '@/core/plugins/perfect-scrollbar'
import '@/core/plugins/inline-svg'
import '@/core/plugins/metronic'
import '@/core/plugins/formvalidation'

window.PerfectScrollbar = PerfectScrollbar
Object.keys(rules).forEach(rule => extend(rule, rules[rule]))
localize('en', en)
Vue.component('ValidationProvider', ValidationProvider)
Vue.component('ValidationObserver', ValidationObserver)
Vue.use(VuePluralize)
Vue.use(require('vue-moment'))
Vue.use(VWave)
Vue.component('date-range-picker', DateRangePicker)
Vue.component('multiselect', Multiselect)

// Import global components
import UnderConstruction from '@/view/layout/extras/UnderConstruction.vue'
import NoDataFound from '@/view/layout/extras/NoDataFound.vue'
import FailedDataGet from '@/view/layout/extras/FailedDataGet.vue'

Vue.component('under-construction', UnderConstruction)
Vue.component('no-data-found', NoDataFound)
Vue.component('failed-data-get', FailedDataGet)

// API service init
ApiService.init()

// Create global event
export const eventBus = new Vue()

// Configure application Toast
export const toastOptions = {
  autoHideDelay: 3000,
  solid: true,
  noHoverPause: true,
  headerClass: 'd-none',
  toaster: 'b-toaster-bottom-center',
}

// setup sentry
Sentry.init({
  Vue,
  // dsn: 'http://6585c70ea65b46fe914eff337077e7b2@172.16.20.68:9000/3',
  dsn: 'https://6585c70ea65b46fe914eff337077e7b2@sentry.yucatech.software/3',
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracePropagationTargets: [
        'http://172.16.18.8:3000/',  // stage
        'http://172.16.20.47:3000/', // preprod
        'http://172.16.20.64:3000/', // prod
        /^\//
      ],
    }),
    new Sentry.Integrations.Breadcrumbs({
      console: false,
    })
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
})

// perform any additional requests in dispatchBefore router hook
router.beforeEach((to, from, next) => {
  const req = []

  if (to.meta.dispatchBefore) {
    store.dispatch(to.meta.dispatchBefore)
  }

  // Ensure we checked auth before each page load
  // If page requires specific data, first make GET request
  Promise.all(req).then(next)

  // reset config to initial state
  store.dispatch(RESET_LAYOUT_CONFIG)

  // Scroll page to top on every route change
  setTimeout(() => {
    window.scrollTo(0, 0)
  }, 100)
})

// apply meta.title on each page
// eslint-disable-next-line no-unused-vars
router.afterEach((to, from) => {
  Vue.nextTick(() => {
    document.title = to.meta.title ? `${to.meta.title} | Hexallion VPN Admin Panel` : 'Hexallion VPN Admin Panel'
  })
})

// console styles
const sharedStyles = 'color: #343a40; padding: 4px 8px; border-radius: 12px; font-weight: 600;'
export const logStyles = {
  version: `background: #F3F6F9; ${sharedStyles}`,
  router:  `background: #64B5F6; ${sharedStyles}`,
  get:     `background: #4CAF50; ${sharedStyles}`,
  post:    `background: #FF5722; ${sharedStyles}`,
  put:     `background: #FF9722; ${sharedStyles}`,
  patch:   `background: #FF9722; ${sharedStyles}`,
  delete:  `background: #FF3D00; ${sharedStyles}`,
  danger:  'background: #FF3D00; color: white; font-size: 20px; padding: 1rem; border-radius: 6px; margin: 20px 0 20px 0;',
}

// setup axios interceptors
axios.interceptors.response.use(response => {
  if (process.env.VUE_APP_ENV === 'production') {
    console.log('%c✋ Warning! This is a browser feature intended for developers. If someone told you to copy and paste something here to enable a feature or "hack" someone\'s account, it is a scam and will give them access to your account.', logStyles.danger)
  }

  if (['local', 'development', 'staging'].includes(process.env.VUE_APP_ENV)) {
    console.log(`%c🗺️ Route Name ⇒ "${router.history.current.name}" | Path ⇒ "${router.history.current.path}"`, logStyles['router'])
    console.log(`%c🌐 ${response.config.method.toUpperCase()} ⇒ ${response.config.baseURL}${response.config.url}`, logStyles[response.config.method])
    console.log(response.data)
    console.log(' '.repeat(50))
  }

  const backendVersion = response.headers['x-applicationversion']
  
  if (backendVersion) {
    store.dispatch(HANDLE_BACKEND_VERSION, backendVersion)
  }

  return response
}, error => {
  // monitor any response error with 401 status (Unauthorized) and logout user if detected
  if (error.response.data.statusCode === 401) {
    store.dispatch(LOGOUT, null, { root: true })
      .then(() => router.push({ name: 'login' }))
      .catch(() => {})
  }
  console.error(error)
  return Promise.reject(error)
})

// disable annoying console tip
Vue.config.productionTip = false

// run application
new Vue({
  router,
  store,
  render: (h) => h(App),
}).$mount('#app')
